import { useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Search, ChevronDown, ChevronUp, AlertTriangle, CheckCircle2, Database, ArrowRight } from 'lucide-react';
import { traceMissingCalls } from '@/lib/api';

export function PipelineTrace() {
  const [isOpen, setIsOpen] = useState(false);
  const [traceData, setTraceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('');

  const runTrace = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (dateFilter) params.date_filter = dateFilter;
      const { data } = await traceMissingCalls(params);
      setTraceData(data);
    } catch (e) {
      console.error('Trace failed:', e);
    } finally {
      setLoading(false);
    }
  }, [dateFilter]);

  return (
    <Card className="card-elevated" data-testid="pipeline-trace">
      <CardHeader 
        className="pb-2 cursor-pointer select-none" 
        onClick={() => { setIsOpen(!isOpen); if (!isOpen && !traceData) runTrace(); }}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Database className="h-4 w-4 text-slate-500" />
            <CardTitle className="text-sm font-medium">Pipeline Trace</CardTitle>
            <span className="text-[10px] text-muted-foreground">Diagnose missing calls</span>
          </div>
          {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </div>
      </CardHeader>
      
      {isOpen && (
        <CardContent className="space-y-3 pt-0">
          {/* Filter + Run */}
          <div className="flex gap-2 items-center">
            <Input 
              type="date" 
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="h-8 text-xs w-40"
              placeholder="Filter by date"
            />
            <Button size="sm" variant="outline" className="h-8 text-xs" onClick={runTrace} disabled={loading}>
              <Search className="h-3 w-3 mr-1" />
              {loading ? 'Tracing...' : 'Run Trace'}
            </Button>
          </div>

          {traceData && (
            <>
              {/* Pipeline Flow */}
              <div className="flex items-center gap-1 text-xs overflow-x-auto pb-1">
                <div className="shrink-0 text-center p-2 bg-blue-50 rounded-lg border border-blue-200 min-w-[80px]">
                  <p className="font-bold text-blue-700">{traceData.synced_files_tracked}</p>
                  <p className="text-blue-500">Synced Files</p>
                </div>
                <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                <div className="shrink-0 text-center p-2 bg-slate-50 rounded-lg border border-slate-200 min-w-[80px]">
                  <p className="font-bold text-slate-700">{traceData.total_calls_in_db}</p>
                  <p className="text-slate-500">Total Calls</p>
                </div>
                <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                <div className="shrink-0 text-center p-2 bg-green-50 rounded-lg border border-green-200 min-w-[80px]">
                  <p className="font-bold text-green-700">{traceData.calls_by_processing_status?.completed || 0}</p>
                  <p className="text-green-500">Completed</p>
                </div>
                <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                <div className="shrink-0 text-center p-2 bg-amber-50 rounded-lg border border-amber-200 min-w-[80px]">
                  <p className="font-bold text-amber-700">{traceData.calls_by_processing_status?.pending || 0}</p>
                  <p className="text-amber-500">Pending</p>
                </div>
                <ArrowRight className="h-3 w-3 text-slate-400 shrink-0" />
                <div className="shrink-0 text-center p-2 bg-red-50 rounded-lg border border-red-200 min-w-[80px]">
                  <p className="font-bold text-red-700">{traceData.calls_by_processing_status?.failed || 0}</p>
                  <p className="text-red-500">Failed</p>
                </div>
              </div>

              {/* Detailed Breakdown */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                <div className="p-2 bg-slate-50 rounded">
                  <p className="text-muted-foreground">Unknown User</p>
                  <p className="font-semibold">{traceData.unknown_user_calls}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded">
                  <p className="text-muted-foreground">Deleted</p>
                  <p className="font-semibold">{traceData.deleted_calls}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded">
                  <p className="text-muted-foreground">No Status</p>
                  <p className="font-semibold">{traceData.no_processing_status + traceData.null_processing_status}</p>
                </div>
                <div className="p-2 bg-slate-50 rounded">
                  <p className="text-muted-foreground">Orphaned Queue</p>
                  <p className="font-semibold">{traceData.orphaned_queue_entries}</p>
                </div>
              </div>

              {/* Status by processing_status */}
              <div className="text-xs space-y-1">
                <p className="font-medium text-slate-600">Calls by Status:</p>
                <div className="flex flex-wrap gap-1">
                  {Object.entries(traceData.calls_by_processing_status || {}).map(([status, count]) => (
                    <Badge key={status} variant="outline" className="text-[10px]">
                      {status}: {count}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Queue Status */}
              {Object.keys(traceData.queue_by_status || {}).length > 0 && (
                <div className="text-xs space-y-1">
                  <p className="font-medium text-slate-600">Queue Status:</p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(traceData.queue_by_status).map(([status, count]) => (
                      <Badge key={status} variant="outline" className="text-[10px]">
                        {status}: {count}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}

              {/* Health */}
              <div className="flex items-center gap-2 pt-1 border-t">
                {traceData.diagnosis?.pipeline_healthy ? (
                  <div className="flex items-center gap-1 text-xs text-green-600">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Pipeline healthy — no data gaps detected
                  </div>
                ) : (
                  <div className="space-y-1">
                    {traceData.diagnosis?.issues?.map((issue, i) => (
                      <div key={i} className="flex items-center gap-1 text-xs text-amber-600">
                        <AlertTriangle className="h-3.5 w-3.5 shrink-0" />
                        {issue}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      )}
    </Card>
  );
}
